import { booleanAttribute, Component, ElementRef, EventEmitter, forwardRef, HostListener, Input, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Native searchable select (combobox) — drop-in replacement for the Material
 * `mat-select` + `mat-select-filter` combination. Implements ControlValueAccessor
 * so it works with `formControlName` / `[(ngModel)]`. The form value is the option's
 * `valueField` (or the whole item when `valueField` is omitted). Supports single and
 * `multiple` selection and optional per-option disabling via `disabledPredicate`.
 */
@Component({
  selector: 'app-searchable-select',
  templateUrl: './searchable-select.component.html',
  styleUrls: ['./searchable-select.component.css'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SearchableSelectComponent),
      multi: true,
    },
  ],
})
export class SearchableSelectComponent implements ControlValueAccessor {
  @Input() items: any[] = [];
  @Input() displayField: string;
  @Input() valueField: string;
  @Input() placeholder: string = 'Select…';
  @Input() searchPlaceholder: string = 'Search…';
  // booleanAttribute: a bare `multiple` attribute (no binding) arrives as the empty
  // string, which is falsy — without the transform the component silently stays in
  // single-select mode and emits a lone object where consumers expect an array.
  @Input({ transform: booleanAttribute }) multiple: boolean = false;
  /** Optional predicate; when it returns true for an item that option is shown but not selectable. */
  @Input() disabledPredicate: (item: any) => boolean;
  /** Emits the newly selected value — the item/valueField for single, the array for multiple. */
  @Output() selectionChange = new EventEmitter<any>();

  public isOpen: boolean = false;
  public disabled: boolean = false;
  public searchTerm: string = '';
  public selectedValue: any = null;

  // Fires when the @if renders the panel's search input — focus it so the user can
  // type immediately after opening the dropdown.
  @ViewChild('searchInput') set searchInput(ref: ElementRef<HTMLInputElement>) {
    ref?.nativeElement.focus();
  }

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private elementRef: ElementRef) {}

  get filteredItems(): any[] {
    const term = (this.searchTerm || '').toLowerCase();
    const list = this.items || [];
    if (!term) {
      return list;
    }
    return list.filter(item => this.getLabel(item).toLowerCase().includes(term));
  }

  get selectedLabel(): string {
    const list = this.items || [];
    if (this.multiple) {
      const arr = Array.isArray(this.selectedValue) ? this.selectedValue : [];
      return list
        .filter(i => arr.some(v => this.valueEquals(this.getValue(i), v)))
        .map(i => this.getLabel(i))
        .join(', ');
    }
    const item = list.find(i => this.valueEquals(this.getValue(i), this.selectedValue));
    return item ? this.getLabel(item) : '';
  }

  public getLabel(item: any): string {
    if (item == null) {
      return '';
    }
    return this.displayField ? (item[this.displayField] ?? '') : String(item);
  }

  private getValue(item: any): any {
    return this.valueField ? item[this.valueField] : item;
  }

  private valueEquals(a: any, b: any): boolean {
    if (a == null || b == null) {
      return false;
    }
    // Primitive/id values (with valueField) compare type-tolerantly; object values
    // (no valueField) compare by reference, matching mat-select's default.
    return this.valueField ? String(a) === String(b) : a === b;
  }

  public isSelected(item: any): boolean {
    const val = this.getValue(item);
    if (this.multiple) {
      const arr = Array.isArray(this.selectedValue) ? this.selectedValue : [];
      return arr.some(v => this.valueEquals(v, val));
    }
    return this.valueEquals(val, this.selectedValue);
  }

  public isDisabled(item: any): boolean {
    return this.disabledPredicate ? this.disabledPredicate(item) : false;
  }

  public toggle(): void {
    if (this.disabled) {
      return;
    }
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.searchTerm = '';
    }
    this.onTouched();
  }

  public onSearch(event: Event): void {
    this.searchTerm = (event.target as HTMLInputElement).value;
  }

  public select(item: any): void {
    if (this.disabled || this.isDisabled(item)) {
      return;
    }
    const val = this.getValue(item);
    if (this.multiple) {
      const arr = Array.isArray(this.selectedValue) ? [...this.selectedValue] : [];
      const idx = arr.findIndex(v => this.valueEquals(v, val));
      if (idx >= 0) {
        arr.splice(idx, 1);
      } else {
        arr.push(val);
      }
      this.selectedValue = arr;
      this.onChange(arr);
      this.selectionChange.emit(arr);
      // keep the panel open for multi-select
    } else {
      this.selectedValue = val;
      this.onChange(val);
      this.selectionChange.emit(val);
      this.isOpen = false;
    }
    this.onTouched();
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
    }
  }

  @HostListener('keydown.escape')
  public onEscape(): void {
    this.isOpen = false;
  }

  // ControlValueAccessor
  public writeValue(value: any): void {
    if (this.multiple) {
      this.selectedValue = Array.isArray(value) ? value : value == null ? [] : [value];
    } else {
      this.selectedValue = value;
    }
  }

  public registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
